import React, { FC } from "react";
import { FlexBox } from "components/flex-box";
import mapCategoryIcon from "./MapCategory";
import { PCBuildRow } from "modules/pcbuilder/types";
import { H4, Span } from "components/Typography";
import styled from "@emotion/styled";
import Box from "@mui/material/Box";
import { formatCurrency, formatCurrencySRP } from "common";
import LazyImage from "components/LazyImage";
import { Grid, Typography } from "@mui/material";
import { BuildPartProperties } from "components/BuildPartProperties";
import { useAppSelector } from "hooks/use-app-selector";
import modules from "modules";
import { isEmpty } from "lodash";

interface Props {
  data: PCBuildRow;
}

const ImageWrapper = styled(Box)(({ theme }) => ({
  textAlign: "center",
  position: "relative",
  display: "inline-block",
}));

const { selectors: pcBuilderSelectors } = modules.pcBuilder;

export const ScreenshotRow: FC<Props> = ({ data }) => {
  const buildPart = useAppSelector(
    pcBuilderSelectors.selectBuildPartFactory(data?.name)
  );

  const showQuantity = buildPart?.quantity > 1;

  const retailPrice = formatCurrency(
    buildPart?.retail_price * buildPart?.quantity
  );

  const srp = formatCurrencySRP(buildPart?.retail_price * buildPart?.quantity);

  if (data?.multiple && !isEmpty(buildPart?.multipleParts)) {
    return (
      <>
        {buildPart?.multipleParts?.map((bp, i) => (
          <Grid
            key={i}
            container
            style={{ borderBottom: "1px solid #C1CDD7", padding: "1em 0" }}
          >
            <Grid
              item
              xs={2}
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <FlexBox flexDirection="column" alignItems="center">
                {mapCategoryIcon(data?.name)}
                <FlexBox
                  width={100}
                  mt={1}
                  py={0}
                  px={2}
                  bgcolor={data.bgColor}
                  alignItems="center"
                  justifyContent="center"
                  border="1px solid #333333"
                  borderRadius={2}
                >
                  <Span color="grey.900" fontWeight={600} fontSize={10}>
                    {data?.name}
                  </Span>
                </FlexBox>
              </FlexBox>
            </Grid>

            <Grid item xs={3}>
              <FlexBox
                flex={1.6}
                alignItems="center"
                justifyContent="center"
                flexDirection={"column"}
              >
                <ImageWrapper
                  width={200}
                  height={200}
                  alignItems="center"
                  justifyContent="center"
                  overflow="hidden"
                >
                  <a>
                    <LazyImage
                      loading="eager"
                      src={bp?.image_url || "/assets/images/sad_red_hair.png"}
                      layout="fill"
                      objectFit="contain"
                    />
                  </a>
                </ImageWrapper>
              </FlexBox>
            </Grid>
            <Grid item xs={5}>
              <BuildPartProperties
                isScreenshot={true}
                product_uuid={bp?.product_uuid}
                custom_specs={bp?.custom_specs}
                product_name={bp?.product_name}
                parts_properties={bp?.parts_properties}
                productSlug={bp?.product_id}
              />
            </Grid>
            <Grid item xs={2} style={{ textAlign: "center" }}>
              <FlexBox flexDirection="column">
                <a>
                  <>
                    <H4 color="grey.900" fontSize={20} fontWeight="700">
                      {formatCurrency(bp?.retail_price * bp?.quantity)}
                    </H4>
                    <Span
                      color="grey.700"
                      sx={{ textDecoration: "line-through" }}
                    >
                      {formatCurrencySRP(bp?.retail_price * bp?.quantity)}
                    </Span>
                  </>
                </a>
                {bp?.quantity > 1 ? (
                  <Typography fontWeight={900} mt="1em" fontSize="1.2em">
                    {`Qty: `}
                    <Span mx={1} fontWeight={600}>
                      {bp?.quantity}
                    </Span>
                  </Typography>
                ) : null}
              </FlexBox>
            </Grid>
          </Grid>
        ))}
      </>
    );
  }

  return (
    <Grid
      container
      style={{ borderBottom: "1px solid #C1CDD7", padding: "1em 0" }}
    >
      <Grid
        item
        xs={2}
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <FlexBox flexDirection="column" alignItems="center">
          {mapCategoryIcon(data?.name)}
          <FlexBox
            width={100}
            mt={1}
            py={0}
            px={2}
            bgcolor={data.bgColor}
            alignItems="center"
            justifyContent="center"
            border="1px solid #333333"
            borderRadius={2}
          >
            <Span color="grey.900" fontWeight={600} fontSize={10}>
              {data?.name}
            </Span>
          </FlexBox>
        </FlexBox>
      </Grid>

      <Grid item xs={3}>
        <FlexBox
          flex={1.6}
          alignItems="center"
          justifyContent="center"
          flexDirection={"column"}
        >
          <ImageWrapper
            width={200}
            height={200}
            alignItems="center"
            justifyContent="center"
            overflow="hidden"
          >
            <a>
              <LazyImage
                loading="eager"
                src={buildPart?.image_url || "/assets/images/sad_red_hair.png"}
                layout="fill"
                objectFit="contain"
              />
            </a>
          </ImageWrapper>
        </FlexBox>
      </Grid>
      <Grid item xs={5}>
        <BuildPartProperties
          isScreenshot={true}
          product_uuid={buildPart?.product_uuid}
          custom_specs={buildPart?.custom_specs}
          product_name={buildPart?.product_name}
          parts_properties={buildPart?.parts_properties}
          productSlug={buildPart?.product_id}
        />
      </Grid>
      <Grid item xs={2} style={{ textAlign: "center" }}>
        <FlexBox flexDirection="column">
          <a>
            <>
              <H4 color="grey.900" fontSize={20} fontWeight="700">
                {retailPrice}
              </H4>
              <Span color="grey.700" sx={{ textDecoration: "line-through" }}>
                {srp}
              </Span>
            </>
          </a>

          {showQuantity ? (
            <Typography fontWeight={900} mt="1em" fontSize="1.2em">
              {`Qty: `}
              <Span mx={1} fontWeight={600}>
                {buildPart?.quantity}
              </Span>
            </Typography>
          ) : null}
        </FlexBox>
      </Grid>
    </Grid>
  );
};
